import type { ComponentContentSchema } from '@vibesites/runtime';
import {
  AmenitiesListCommunity as BaseAmenitiesListCommunity,
  contentSchema as baseSchema,
} from '@vibesites/theme-via6/components/AmenitiesListCommunity';

/**
 * The Majestic — AmenitiesListCommunity override (community amenities row).
 *
 * Wraps the Weidner Via6 base component (which delegates rendering to the
 * shared `_AmenitiesListRow` helper) and layers two brand tweaks that both
 * live inside the parent's opaque JSX tree — so a CSS style wrapper is the
 * cheapest override that reaches them (no need to fork `_AmenitiesListRow`):
 *
 *   1. Capitalize only the FIRST letter of each amenity list item (sentence
 *      case), leaving the rest of the title's casing untouched. `::first-letter`
 *      needs a block container, so the item's text span is promoted to
 *      inline-block; the rule applies to both the visible list and the hidden
 *      measurement copy, so pagination height math stays consistent.
 *   2. Hide the "Amenities will be displayed here." empty-state placeholder
 *      (`.weidner-amenities-row-empty`) so an empty group renders nothing
 *      instead of the base fallback copy.
 *
 * Both selectors are scoped to `[data-weidner-amenities-row]` — the stable
 * data-attribute the shared row section carries — so they only affect these
 * amenity rows and can't leak into unrelated sections.
 *
 * The base schema travels with us via the re-attach below — without it the
 * editor would show no editing affordances on the wrapper.
 */
export function AmenitiesListCommunity() {
  return (
    <>
      <BaseAmenitiesListCommunity />
      <style>{`
        /* Capitalize only the FIRST letter of each amenity item (sentence
           case), leaving the rest of the title's casing untouched.
           ::first-letter needs a block container, so the text span (the
           last child of the flex <li>; the preceding bullet span is
           display:none) is promoted to inline-block. */
        [data-weidner-amenities-row] .amenities-col-item span:last-child {
          display: inline-block;
        }
        [data-weidner-amenities-row] .amenities-col-item span:last-child::first-letter {
          text-transform: uppercase;
        }
        [data-weidner-amenities-row] .weidner-amenities-row-empty {
          display: none !important;
        }

        /* Increase amenity list-item font size on mobile for readability.
           The base sets 0.8rem at <=900px and 0.72rem at <=600px; bump both
           up. The base selector [data-weidner-amenities-row] .amenities-col-item
           is global — it also matches the sibling Interior row, whose own copy
           of the base <style> can render after this one and, at equal
           specificity, re-declare the smaller size and cancel our override.
           So we add the parent .amenities-col-list class to raise specificity
           (0,3,0 vs the base's 0,2,0) and win regardless of DOM order. Both the
           visible list and the hidden measurement copy carry .amenities-col-list
           > .amenities-col-item, so pagination height math stays consistent. */
        @media (max-width: 900px) {
          [data-weidner-amenities-row] .amenities-col-list .amenities-col-item {
            font-size: 1rem;
          }
        }
        @media (max-width: 600px) {
          [data-weidner-amenities-row] .amenities-col-list .amenities-col-item {
            font-size: 0.95rem;
          }
        }
      `}</style>
    </>
  );
}

(AmenitiesListCommunity as unknown as { contentSchema: ComponentContentSchema }).contentSchema =
  baseSchema;

export default AmenitiesListCommunity;
